import { useState, useEffect } from 'react';

/**
 * useMediaQuery — subscribe to a CSS media query, returns true while it matches.
 *
 * Reach for this only when a layout change can't be expressed in Tailwind
 * breakpoints — i.e. when an element has to live in a *different DOM parent*
 * per breakpoint. Rendering the same element twice (`hidden`/`sm:hidden`) is
 * usually simpler, but duplicates `data-tour` / `id` attributes and breaks the
 * `document.querySelector` lookups that depend on them being unique.
 *
 * The initial state is read synchronously so the first paint is already
 * correct (no post-mount layout flash).
 */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false)
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    // Re-sync in case the viewport changed between render and effect
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export default useMediaQuery;
